import _ from 'lodash';
import React, {
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { compose } from 'redux';
import { connect } from 'react-redux';

import {
  SAVE_AVAILABILITY,
  SUBMIT_AVAILABILITY,
  GET_AVAILABILTIES_BY_PROJECT,
  GET_COMPANY_MEMBER_BY_USER_ID,
} from '../graphql';

import { config } from '../../../../../config';
import CustomCalendar from '../../../../../core/components/calendar';
import RecruitLinkCard from '../../../../components/recruit-link-card';
import TimeZoneDropdown from '../../../../../core/components/timezone-dropdown';
import { constructScheduleURL } from '../constants';

import {
  Button,
  ButtonContainer,
  Container,
  ContainerWarningTimezone,
  Title,
  Content,
  Header,
  DropdownContainer,
  LabelDropdown,
  Modal,
  ModalParagraph,
  ModalHeader,
  ModalLink,
  WarningTimezoneMessage,
  ContainerLoading,
  Loader,
} from './styles';
import { findCurrentCompanyMember } from '../../../../../core/utils/find-current-company-member';
import { GET_MS_USER_DATA } from './graphql';

const mapEvents = (events) => _.map(events, (event) => ({
  start: event.startAt,
  end: event.endAt,
}));

const Availability = (props) => {
  const {
    onChangeTimezone,
    timeZone,
    project,
    companyId,
    me,
  } = props;

  const {
    createdBy: {
      id: projectCreatorUserId,
    },
    participantsSource,
    submittedAvailabilities,
    testType,
    sessionDuration,
  } = project;

  // we are getting the company members to check if the project creator has an integration of category calendar connected
  const {
    data: companyMembersData,
    loading: loadingCompanyMembersData,
  } = useQuery(GET_COMPANY_MEMBER_BY_USER_ID, {
    variables: {
      userId: projectCreatorUserId,
    },
  });

  // for now we do not show the project creator's calendar events
  const projectCreatorCompanyMember = useMemo(() => findCurrentCompanyMember(companyMembersData?.companyMembers, companyId),
    [companyMembersData, companyId]);

  const { companyServiceIntegrations: currentCompanyMemberIntegrations = [] } = projectCreatorCompanyMember || {};

  const projectCreatorCalendarIntegrations = useMemo(
    () => _.find(currentCompanyMemberIntegrations, (integration) => integration.serviceIntegration.category === 'calendar'),
    [currentCompanyMemberIntegrations],
  );

  const {
    data: projectCreatorIntegrationData,
    loading: loadingProjectCreatorIntegrationData,
  } = useQuery(GET_MS_USER_DATA, {
    variables: {
      companyMemberId: projectCreatorCompanyMember?.id,
    },
    skip: !projectCreatorCompanyMember,
  });

  const { msUserData } = projectCreatorIntegrationData || {};

  const integrationCustomButton = useMemo(() => {
    if (loadingProjectCreatorIntegrationData || loadingCompanyMembersData) return 'loading';
    // the following rule is subject to change:
    // show the connect button if the user is the project creator and doesnt have calendar integrations
    // show the connected message if the user has a calendar integration and the project creator has a connected integration
    const projectCreatorEmail = msUserData?.mail;

    if (projectCreatorCalendarIntegrations && projectCreatorEmail) {
      // just checking projectCreatorCalendarIntegrations is not enough because it can have a status of 'pending' or 'connection failure'
      return {
        name: 'integrationConnected',
        text: `Connected to ${projectCreatorEmail}`,
      };
    }

    if (projectCreatorUserId === me.user_id) {
      return {
        name: 'integrationLink',
        text: 'Connect Calendar',
        click: () => {
          window.location.href = `${config.REACT_APP_COMPANY_PLATFORM_URI}/integrations`;
        },
      };
    }

    return null;
  }, [
    projectCreatorCalendarIntegrations,
    projectCreatorUserId,
    me,
    loadingCompanyMembersData,
    loadingProjectCreatorIntegrationData,
    msUserData,
  ]);

  const url = constructScheduleURL(project);

  const [currentTimezone, setCurrentTimezone] = useState(timeZone);
  const [timezoneName, setTimezoneName] = useState(null);
  const [toggleTime, setToggleTime] = useState(false);

  // Used to automatically get the current user timezone.
  useEffect(() => {
    if (!currentTimezone) {
      const tz = moment.tz.guess();
      setCurrentTimezone(tz);
      onChangeTimezone(tz);
    } else {
      setCurrentTimezone(timeZone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZone]);

  const {
    data = {},
    refetch,
  } = useQuery(GET_AVAILABILTIES_BY_PROJECT, {
    variables: {
      projectId: project.id,
    },
  });

  const [saveAvailabilities, { loading: loadingSaving }] = useMutation(SAVE_AVAILABILITY);
  const [submitAvailabilities] = useMutation(SUBMIT_AVAILABILITY);
  const [modalOpen, setModalOpen] = useState(false);

  const handleSave = async (events) => {
    await saveAvailabilities({
      variables: {
        projectId: project.id,
        attributes: events,
      },
    });
    refetch();
  };

  const handleSubmit = async () => {
    await submitAvailabilities({
      variables: {
        id: project.id,
        attributes: {
          submittedAvailabilities: true,
        },
      },
    });
    refetch();
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleChangeToggle = () => {
    setToggleTime((prev) => !prev);
  };

  const eventAvailabilities = useMemo(() => {
    if (_.isEmpty(data) || _.isEmpty(data.availabilities)) { return []; }

    return mapEvents(data.availabilities);
  }, [data]);

  const renderContent = () => {
    if (participantsSource === 'playbook') {
      return (
        <>
          <ModalParagraph>
            We will share your availability with qualified participants.
          </ModalParagraph>
          <ModalParagraph>
            You can make changes to your schedule at any time.
          </ModalParagraph>
          <ModalParagraph>
            Once a participant books a session, you’ll see their
            information appear under “Confirmed sessions”. You will
            also receive an email invite.
          </ModalParagraph>
        </>
      );
    }
    return (
      <>
        <ModalParagraph>
          Share this link with participants.
        </ModalParagraph>
        <ModalLink>
          <RecruitLinkCard
            testType={testType}
            raw
            url={url}
          />
        </ModalLink>
        <ModalParagraph>
          You can make changes to your schedule at any time.
        </ModalParagraph>
        <ModalParagraph>
          Once a participant books a session, you’ll see their
          information appear under “Confirmed sessions”. You will
          also receive an email invite.
        </ModalParagraph>
      </>
    );
  };

  if (!currentTimezone) return <></>;

  return (
    <Container>
      <Header>
        <Title>
          Availability
        </Title>

        <DropdownContainer>
          <LabelDropdown>Timezone:</LabelDropdown>
          <TimeZoneDropdown
            onChange={onChangeTimezone}
            toggleTime={toggleTime}
            onChangeToggle={handleChangeToggle}
            onSetTimezoneName={setTimezoneName}
            timeZone={currentTimezone}
          />
        </DropdownContainer>
      </Header>

      <ContainerLoading>
        {loadingSaving && <Loader data-loader="availability-loader" />}
      </ContainerLoading>

      <Content>
        <CustomCalendar
          integrationCustomButton={integrationCustomButton}
          events={eventAvailabilities}
          handleSave={handleSave}
          handleSubmit={handleSubmit}
          showSubmitButton={!submittedAvailabilities}
          toggleTime={toggleTime}
          timeZone={currentTimezone}
          loading={loadingSaving}
          slotDuration="00:15:00"
          sessionDuration={sessionDuration}
        />
      </Content>
      <Modal
        size="tiny"
        open={modalOpen}
        closeOnEscape
        closeOnDimmerClick
        onClose={handleClose}
      >
        <Modal.Content>
          <ModalHeader>Thanks for submitting your availability!</ModalHeader>
          {renderContent()}
          <ContainerWarningTimezone>
            <WarningTimezoneMessage>
              {`Your timezone is set to ${timezoneName}.
              If that is not correct, you can update your timezone in the dropdown above the calendar.`}
            </WarningTimezoneMessage>
          </ContainerWarningTimezone>
          <ButtonContainer>
            <Button
              onClick={handleClose}
            >
              OK
            </Button>
          </ButtonContainer>
        </Modal.Content>
      </Modal>
    </Container>
  );
};

Availability.propTypes = {
  onChangeTimezone: PropTypes.func.isRequired,
  timeZone: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  me: PropTypes.shape({
    user_id: PropTypes.string.isRequired,
  }).isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
    availabilities: PropTypes.arrayOf(PropTypes.shape({})),
    createdBy: PropTypes.shape({
      id: PropTypes.string,
    }),
    participantsSource: PropTypes.string,
    submittedAvailabilities: PropTypes.bool,
    testType: PropTypes.string,
    segments: PropTypes.PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
    recruitParticipantToken: PropTypes.shape({
      token: PropTypes.string,
    }),
    surveys: PropTypes.arrayOf(PropTypes.shape({
      surveyType: PropTypes.string,
    })),
    sessionDuration: PropTypes.number,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  me: authentication.profile,
});

const enhance = compose(
  connect(mapStateToProps, null),
);

export default enhance(Availability);
